import React from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import useVoyagerStore from '../../stores/useVoyagerStore';
import type { LandingAgentTurn } from '../../stores/slices/landingAgentSlice';
import type { DefaultItem, LadderAnswer } from '../Ladder/claims';
import { readingOf } from '../../stores/slices/progress';
import type { SxProps, Theme } from '@mui/material/styles';

/** The chips of the defaults an answer stated (#1808): each the server's words, its sentence as the title, and — when
 *  `changeable` — a menu of its server-issued options. Shared by this line and F4's reading line (#1858), never two. */
export function DefaultChips({ turn, items, changeable, sx, chevron = false }: {
  turn: LandingAgentTurn;
  items: Array<Pick<DefaultItem, 'id' | 'label' | 'sentence'> & { options?: DefaultItem['options'] }>;
  changeable: boolean;
  /** The reading line's words, in place of the chip's pill. */
  sx?: SxProps<Theme>;
  /** With `sx`: a changeable chip ends in a small chevron (an icon, so its text is the label alone). */
  chevron?: boolean;
}) {
  const changeDefault = useVoyagerStore((s) => s.changeDefault);
  const [open, setOpen] = React.useState<{ anchor: HTMLElement; item: (typeof items)[number] } | null>(null);
  const choose = (item: (typeof items)[number], option: number) => {
    setOpen(null);
    changeDefault(turn.id, item.id, option);
  };
  return (
    <>
      {items.map((item) => {
        // an item with no options (#1818: the label-free "Reading not settled") has no control
        const can = changeable && (item.options || []).length > 0;
        return (
          <Chip key={item.id} size="small" variant="outlined" data-testid={`default-chip-${item.id}`}
                title={item.sentence || item.label} aria-label={item.sentence || item.label}
                label={can && !sx ? `${item.label} ▾` : can && chevron
                  ? <>{item.label}<ExpandMoreIcon aria-hidden sx={{ fontSize: 14 }} /></> : item.label}
                aria-haspopup={can ? 'menu' : undefined}
                onClick={can ? (e) => setOpen({ anchor: e.currentTarget, item }) : undefined}
                sx={sx || { borderRadius: 99, borderColor: 'var(--line, #ccc)', color: 'var(--ink-2)', maxWidth: '100%' }} />
        );
      })}
      <Menu anchorEl={open?.anchor || null} open={!!open} onClose={() => setOpen(null)}>
        {(open?.item.options || []).map((o) => (
          <MenuItem key={o.option} selected={!!o.current} disabled={!!o.current}
                    data-testid={`default-option-${open!.item.id}-${o.option}`}
                    onClick={() => choose(open!.item, o.option)}>
            {o.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

/** Whether this viewer may change the answer's stated defaults: F3's own contract (the ladder's block, the turn's ask). */
export function defaultsChangeable(turn: LandingAgentTurn): boolean {
  const block = (turn.result?.ladder as LadderAnswer | null | undefined)?.defaults;
  return block?.changeable === true && !!turn.askedRequest;
}

/**
 * The defaults an answer stated (#1808, F3 §3): one chip per item — "Last 4 weeks · Sep 27 – Oct 24, 2026",
 * "Region: Market Region" — each the server's words for this viewer, its sentence as the chip's title, and a menu of
 * its server-issued options. A choice re-sends the turn's own request with `default_change` (references only) and
 * `replaces`, so the turn is replaced in place. Another person's answer shows the chips with no menu.
 * `chips={false}` (F4, #1858): the reading line shows these chips, so this line keeps only the sentences and notice —
 * and the chip of a reading item (#1818) no part of the reading line shows.
 */
export default function DefaultsLine({ turn, chips = true }: { turn: LandingAgentTurn; chips?: boolean }) {
  const block = (turn.result?.ladder as LadderAnswer | null | undefined)?.defaults;
  const items = (block?.items || []).filter((i) => i && typeof i.sentence === 'string');
  if (!items.length) return null;
  return (
    <Box data-testid="defaults-line" sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', minWidth: 0 }}>
      {(() => {
        const shown = chips ? null : new Set(readingOf(turn).flatMap((p) => (p.defaults || []).map((d) => d.id)));
        const own = shown ? items.filter((i) => (i.kind === 'reading' || i.kind === 'plain') && !shown.has(i.id)) : items;
        return own.length > 0 && <DefaultChips turn={turn} items={own} changeable={defaultsChangeable(turn)} />;
      })()}
      {/* the canonical statement: the same sentences the ladder's caveats, Slack and MCP carry */}
      {items.some((i) => i.sentence) && <Typography variant="caption" data-testid="defaults-said"
                  sx={{ color: 'var(--ink-3)', flexBasis: '100%', minWidth: 0, overflowWrap: 'anywhere' }}>
        {items.map((i) => i.sentence).filter(Boolean).join(' ')}
      </Typography>}
      {turn.defaultsNotice && (
        <Typography variant="caption" data-testid="defaults-notice" sx={{ color: 'var(--ink-3)' }}>
          {turn.defaultsNotice}
        </Typography>
      )}
    </Box>
  );
}
